import type { NavGate, NavGroupConfig } from './types';

export interface FilterContext {
  isInterim: boolean;
  isAdmin: boolean;
  hasPermission: (q: { module: string; feature: string }) => boolean;
  hasModule: (m: string) => boolean;
}

/**
 * Check whether a gate is satisfied by the given filter context.
 * Gates combine additively (AND) — every provided constraint must pass.
 */
export function passesGate(gate: NavGate, ctx: FilterContext): boolean {
  if (gate.adminOnly && !ctx.isAdmin) return false;
  if (gate.tenantType === 'interim' && !ctx.isInterim) return false;
  if (gate.permission && !ctx.hasPermission(gate.permission)) return false;
  if (gate.moduleAvailable && !ctx.hasModule(gate.moduleAvailable)) return false;
  return true;
}

/**
 * Filter nav groups + items through a filter context.
 * - Groups that fail their own gate are dropped entirely.
 * - Items that fail their gate are dropped.
 * - Sub-groups are filtered recursively; empty sub-groups are dropped.
 * - Groups with no remaining items AND no remaining sub-groups are dropped.
 */
export function filterNav(groups: NavGroupConfig[], ctx: FilterContext): NavGroupConfig[] {
  return groups
    .filter((g) => passesGate(g, ctx))
    .map((g) => {
      const items = g.items.filter((i) => passesGate(i, ctx));
      const subGroups = g.subGroups
        ? g.subGroups
            .map((sg) => ({ ...sg, items: sg.items.filter((i) => passesGate(i, ctx)) }))
            .filter((sg) => sg.items.length > 0)
        : undefined;
      return { ...g, items, subGroups };
    })
    .filter((g) => g.items.length > 0 || (g.subGroups && g.subGroups.length > 0));
}
